

import { useState } from "react"
import { MathText, truncateText } from "./mathRenderer"
import StudyPage from "./StudyPage"
import Modal from "./SummaryModal"

function QuestionsTable({ questions, isUploading, isGrading, onGrade, onRemove, onGradeAll }) {
  const [expandedRows, setExpandedRows] = useState({})
  const [showSummary, setShowSummary] = useState(false)
  const [summaryData, setSummaryData] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [showStudyPage, setShowStudyPage] = useState(false)

  // Toggle expanded state for a row
  const toggleRowExpand = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  // Update the handleGradeAllWithSummary function to wait for state updates
  const handleGradeAllWithSummary = async (ungradedQuestions) => {
    if (ungradedQuestions.length === 0) {
      alert("No questions to grade.")
      return
    }

    // First, grade all questions
    const gradedResults = await onGradeAll(ungradedQuestions)

    // Only generate summary if we have results
    if (gradedResults && gradedResults.length > 0) {
      // Use the actual graded results instead of relying on state
      generateSummaryFromResults(gradedResults)
    } else {
      // Wait a bit for state to update and then try again with the state
      setTimeout(() => {
        generateSummary()
      }, 1000)
    }
  }

  // Add a new function that generates summary from direct results
  const generateSummaryFromResults = (gradedResults) => {
    setSummaryLoading(true)

    if (gradedResults.length === 0) {
      alert("No graded questions found. Please grade some questions first.")
      setSummaryLoading(false)
      return
    }

    const totalQuestions = gradedResults.length
    let totalScore = 0
    let totalMaxScore = 0

    // Calculate total scores
    gradedResults.forEach((q) => {
      totalScore += Number.parseFloat(q.grade || 0)
      totalMaxScore += Number.parseFloat(q.maxgrade || 0)
    })

    // Calculate average score as percentage
    const averagePercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0

    // Generate summary data
    const summary = {
      folderName: "Current Grading Session",
      totalQuestions,
      totalScore,
      totalMaxScore,
      averagePercentage,
      questions: gradedResults.map((q, index) => ({
        questionNumber: q.questionNumber || index + 1,
        score: `${q.grade}/${q.maxgrade}`,
        scorePercentage:
          Number.parseFloat(q.maxgrade) > 0
            ? Math.round((Number.parseFloat(q.grade) / Number.parseFloat(q.maxgrade)) * 100)
            : 0,
        feedback: q.feedback,
        questionContent: q.questionContent,
        studentSolution: q.studentSolution,
      })),
    }

    setSummaryData(summary)
    setShowSummary(true)
    setSummaryLoading(false)
  }

  // Generate summary for graded questions
  const generateSummary = () => {
    setSummaryLoading(true)

    // Calculate summary data
    setTimeout(() => {
      // Get only the graded questions
      const gradedQuestions = questions.filter((q) => q.grade !== undefined && q.grade !== null)

      if (gradedQuestions.length === 0) {
        alert("No graded questions found. Please grade some questions first.")
        setSummaryLoading(false)
        return
      }

      const totalQuestions = gradedQuestions.length
      let totalScore = 0
      let totalMaxScore = 0

      // Calculate total scores
      gradedQuestions.forEach((q) => {
        totalScore += Number.parseFloat(q.grade || 0)
        totalMaxScore += Number.parseFloat(q.maxgrade || 0)
      })

      // Calculate average score as percentage
      const averagePercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0

      // Generate summary data
      const summary = {
        folderName: "Current Grading Session",
        totalQuestions,
        totalScore,
        totalMaxScore,
        averagePercentage,
        questions: gradedQuestions.map((q, index) => ({
          questionNumber: q.questionNumber || index + 1,
          score: `${q.grade}/${q.maxgrade}`,
          scorePercentage:
            Number.parseFloat(q.maxgrade) > 0
              ? Math.round((Number.parseFloat(q.grade) / Number.parseFloat(q.maxgrade)) * 100)
              : 0,
          feedback: q.feedback,
          questionContent: q.questionContent,
          studentSolution: q.studentSolution,
        })),
      }

      setSummaryData(summary)
      setShowSummary(true)
      setSummaryLoading(false)
    }, 500) // Simulate processing time
  }

  // Download summary as text file
  const downloadSummary = (format = "txt") => {
    if (!summaryData) return

    if (format === "txt") {
      // Create text content
      let content = `# Summary for ${summaryData.folderName}\n\n`
      content += `Total Questions: ${summaryData.totalQuestions}\n`
      content += `Total Score: ${summaryData.totalScore}/${summaryData.totalMaxScore} (${summaryData.averagePercentage}%)\n\n`

      // Add details for each question
      summaryData.questions.forEach((q, index) => {
        content += `## Question ${q.questionNumber || index + 1}\n`
        content += `Score: ${q.score} (${q.scorePercentage}%)\n`
        content += `Question: ${q.questionContent}\n`
        content += `Student Solution: ${q.studentSolution}\n`
        content += `Feedback: ${q.feedback}\n\n`
      })

      // Create and trigger download
      const blob = new Blob([content], { type: "text/plain" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `Grading_Session_Summary.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } else if (format === "pdf") {
      // For PDF, we'll use a simpler approach - print the modal content
      printSummary()
    }
  }

  // Print the summary modal content
  const printSummary = () => {
    if (!summaryData) return

    setIsPrinting(true)

    // We need to wait a bit for the printing class to be applied
    setTimeout(() => {
      window.print()

      // Remove the printing class after printing is done
      setTimeout(() => {
        setIsPrinting(false)
      }, 500)
    }, 100)
  }

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "text-green-600"
    if (percentage >= 60) return "text-yellow-600"
    return "text-red-600"
  }

  return (
    <section className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100 backdrop-blur-sm bg-white/90 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 animate-fadeIn">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-blue-50 to-transparent">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            Questions
          </h2>
          <p className="text-sm text-gray-500 ml-7">View and grade uploaded exam questions</p>
        </div>
        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full h-fit">
          {questions.length} Total
        </span>
      </div>
      <div className="overflow-x-auto">
        {isUploading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <svg
              className="animate-spin h-12 w-12 text-blue-500 mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-600 text-lg">Processing questions...</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"
                >
                  #
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Question Content
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Student's Solution
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Marking Scheme
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28"
                >
                  <div className="flex items-center justify-between">
                    <span>Action</span>
                    <button
                      onClick={() => handleGradeAllWithSummary(questions.filter((q) => !q.grade))}
                      disabled={isGrading !== null || questions.filter((q) => !q.grade).length === 0}
                      className={`ml-2 ${
                        isGrading !== null || questions.filter((q) => !q.grade).length === 0
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-300"
                      } text-white text-xs font-medium py-1 px-2 rounded-md flex items-center justify-center transition-all duration-200 transform hover:scale-105 active:scale-95`}
                      title="Grade all ungraded questions"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Grade All
                    </button>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36"
                >
                  Grade
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"
                >
                  Remove
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isUploading ? (
                <tr>
                  <td colSpan="7" className="py-8">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="animate-spin h-12 w-12 text-blue-500 mb-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <p className="text-gray-600 text-lg">Processing questions...</p>
                    </div>
                  </td>
                </tr>
              ) : questions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12 text-gray-300 mb-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-gray-500 mb-1">No questions found</p>
                      <p className="text-gray-400 text-sm">Upload some questions to get started</p>
                    </div>
                  </td>
                </tr>
              ) : (
                questions.map((q, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <div className={expandedRows[index] ? "" : "line-clamp-2 max-w-xs"}>
                          {expandedRows[index] ? (
                            <MathText text={q.questionContent} className="math-content" />
                          ) : (
                            <MathText text={truncateText(q.questionContent, 100)} className="math-content" />
                          )}
                        </div>
                        <button
                          onClick={() => toggleRowExpand(index)}
                          className="self-start mt-1 text-blue-500 hover:text-blue-700 text-xs font-medium whitespace-nowrap bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded transition-colors duration-200"
                        >
                          {expandedRows[index] ? (
                            <span className="flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                              Show Less
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              Show More
                            </span>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <div className={expandedRows[index] ? "" : "line-clamp-2 max-w-xs"}>
                          {expandedRows[index] ? (
                            <MathText text={q.studentSolution} className="math-content" />
                          ) : (
                            <MathText text={truncateText(q.studentSolution, 100)} className="math-content" />
                          )}
                        </div>
                        <button
                          onClick={() => toggleRowExpand(index)}
                          className="self-start mt-1 text-blue-500 hover:text-blue-700 text-xs font-medium whitespace-nowrap bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded transition-colors duration-200"
                        >
                          {expandedRows[index] ? (
                            <span className="flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                              Show Less
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              Show More
                            </span>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className={expandedRows[index] ? "" : "line-clamp-2 max-w-xs"}>
                        {expandedRows[index] ? (
                          <MathText text={q.markingScheme || "N/A"} className="math-content" />
                        ) : (
                          <MathText text={truncateText(q.markingScheme, 100) || "N/A"} className="math-content" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {q.grade ? (
                        <span className="inline-flex items-center text-green-600 font-medium text-xs">
                          <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          Graded
                        </span>
                      ) : (
                        <button
                          onClick={() => onGrade(q, index)}
                          disabled={isGrading !== null}
                          className={`${
                            isGrading === index
                              ? "bg-green-400 cursor-wait"
                              : "bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-300"
                          } text-white text-xs font-medium py-1.5 px-3 rounded-md flex items-center justify-center min-w-[100px] transition-all duration-200 transform hover:scale-105 active:scale-95`}
                        >
                          {isGrading === index ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Grading...
                            </>
                          ) : (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              Start Grading
                            </>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {q.grade ? (
                        <div className="text-gray-700">
                          <span className="block font-medium text-sm">
                            Score:{" "}
                            <span className="text-blue-600">
                              {q.grade}/{q.maxgrade}
                            </span>
                          </span>
                          <span className="block text-xs text-gray-500 line-clamp-2">
                            {expandedRows[index] ? q.feedback : truncateText(q.feedback, 60)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">Not graded</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => onRemove(index)}
                        className="text-gray-400 hover:text-red-500 focus:outline-none transition-colors duration-200 transform hover:scale-110"
                        title="Remove question"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary Modal using Portal */}
      <Modal isOpen={showSummary && summaryData} onClose={() => setShowSummary(false)}>
        <div className="p-5 border-b border-gray-200 print:border-b-2">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800 print:text-2xl">Grading Summary</h3>
            <button onClick={() => setShowSummary(false)} className="text-gray-400 hover:text-gray-600 print:hidden">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto flex-grow print:overflow-visible print:p-8">
          <div className="bg-blue-50 p-4 rounded-lg mb-4 print:bg-blue-50 print:border print:border-blue-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 print:text-base">Total Questions</p>
                <p className="text-xl font-bold text-gray-800 print:text-2xl">{summaryData?.totalQuestions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 print:text-base">Average Score</p>
                <p className="text-xl font-bold text-gray-800 print:text-2xl">{summaryData?.averagePercentage}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 print:text-base">Total Score</p>
                <p className="text-xl font-bold text-blue-600 print:text-2xl">
                  {summaryData?.totalScore}/{summaryData?.totalMaxScore}
                </p>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-gray-700 mb-3 print:text-xl print:mt-6">Question Details</h4>

          <div className="space-y-4 print:space-y-8">
            {summaryData?.questions.map((q, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-3 print:p-4 print:border-2 print:page-break-inside-avoid"
              >
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium text-gray-800 print:text-lg">Question {q.questionNumber || idx + 1}</h5>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium print:text-sm print:py-1 print:px-3 ${
                      q.scorePercentage >= 80
                        ? "bg-green-100 text-green-800 print:bg-green-100"
                        : q.scorePercentage >= 60
                          ? "bg-yellow-100 text-yellow-800 print:bg-yellow-100"
                          : "bg-red-100 text-red-800 print:bg-red-100"
                    }`}
                  >
                    {q.score} ({q.scorePercentage}%)
                  </span>
                </div>

                <div className="text-sm text-gray-600 mb-2 print:text-base print:mb-4">
                  <p className="font-medium mb-1 print:mb-2">Question:</p>
                  <div className="pl-3 border-l-2 border-gray-200 print:pl-4">
                    <MathText text={q.questionContent} className="math-content print:text-base" />
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-2 print:text-base print:mb-4">
                  <p className="font-medium mb-1 print:mb-2">Student Solution:</p>
                  <div className="pl-3 border-l-2 border-gray-200 print:pl-4">
                    <MathText text={q.studentSolution} className="math-content print:text-base" />
                  </div>
                </div>

                <div className="text-sm text-gray-600 print:text-base">
                  <p className="font-medium mb-1 print:mb-2">Feedback:</p>
                  <div className="pl-3 border-l-2 border-gray-200 print:pl-4">{q.feedback}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end print:hidden">
          <button
            onClick={() => setShowSummary(false)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium mr-2"
          >
            Close
          </button>
          <div className="flex">
            <button
              onClick={() => downloadSummary("txt")}
              className="px-4 py-2 bg-blue-500 text-white rounded-l-md hover:bg-blue-600 font-medium text-sm focus:ring-2 focus:ring-blue-300 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-4a3 3 0 00-3-3H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Download TXT
            </button>
            <button
              onClick={() => printSummary()}
              className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 font-medium text-sm focus:ring-2 focus:ring-blue-300 flex items-center border-l border-blue-400"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Print PDF
            </button>
            <button
              onClick={() => {
                setShowSummary(false)
                setShowStudyPage(true)
              }}
              className="px-4 py-2 mx-32 bg-gradient-to-r from-purple-800 to-red-500 text-white rounded-md hover:bg-green-600 font-medium text-sm focus:ring-2 focus:ring-green-300 flex items-center ml-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              Study for the Exam
            </button>
          </div>
        </div>
      </Modal>

      {/* Study Page */}
      {showStudyPage && summaryData && <StudyPage summaryData={summaryData} onClose={() => setShowStudyPage(false)} />}

      {/* Print-specific styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .printing, .printing * {
            visibility: visible;
          }
          .printing {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
          .printing > div {
            width: 100%;
            max-width: 100%;
            box-shadow: none;
            border-radius: 0;
            height: auto;
            max-height: none;
          }
          .katex {
            font-size: 1.2em !important;
          }
          .math-content {
            font-size: 14pt !important;
          }
        }
      `}</style>
    </section>
  )
}

export default QuestionsTable
